import Phaser from 'phaser';
import { WALL_CROSSBOW_TOP, WALL_CROSSBOW_BOTTOM, WALL_X, WALL_WIDTH } from '../../config';
import { Enemy } from '../Enemy';
import { Projectile } from '../Projectile';
import { ChainUpgradeEffect, TargetPriority } from '../../types';
import { selectByPriority } from '../units/BaseUnit';

type TurretPosition = 'top' | 'bottom';

const CONFIGS = {
    top: WALL_CROSSBOW_TOP,
    bottom: WALL_CROSSBOW_BOTTOM,
};

const TARGETING: Record<TurretPosition, TargetPriority> = {
    top: 'backline',    // Snipe approaching enemies (furthest)
    bottom: 'nearest',  // Defend the wall (nearest)
};

export class WallCrossbowTurret {
    scene: Phaser.Scene;
    x: number;
    y: number;
    position: TurretPosition;
    unitType: string;
    graphics: Phaser.GameObjects.Graphics;
    private attackTimer: number = 0;
    private projectiles: Projectile[];

    // Chain upgrade bonuses
    bonusDamageMult: number = 1;
    attackIntervalMult: number = 1;
    bonusPierceCount: number = 0;
    bonusRange: number = 0;
    bonusAoeRadius: number = 0;
    globalDamageMult: number = 1;
    attackSpeedMult: number = 1;
    chainLevel: number = 0;

    // Stack upgrade scaling (when purchased in shop)
    stackCount: number = 1;

    constructor(
        scene: Phaser.Scene,
        position: TurretPosition,
        projectiles: Projectile[],
    ) {
        this.scene = scene;
        this.position = position;
        this.unitType = `wall_crossbow_${position}`;
        const config = CONFIGS[position];
        this.x = WALL_X + WALL_WIDTH + 5;
        this.y = config.y;
        this.projectiles = projectiles;
        this.graphics = scene.add.graphics();
        this.graphics.setDepth(200);
        this.draw();
    }

    private get config() {
        return CONFIGS[this.position];
    }

    addStack(): void {
        this.stackCount++;
    }

    getStackMultiplier(): number {
        return this.stackCount;
    }

    getDamage(): number {
        return this.config.baseDamage * this.bonusDamageMult * this.globalDamageMult * this.stackCount;
    }

    get pierceCount(): number {
        return this.config.basePierceCount + this.bonusPierceCount;
    }

    get range(): number {
        return this.config.range + this.bonusRange;
    }

    applyChainUpgrade(effect: ChainUpgradeEffect): void {
        if (effect.damageMultBonus !== undefined) this.bonusDamageMult *= (1 + effect.damageMultBonus);
        if (effect.attackIntervalMult !== undefined) this.attackIntervalMult *= effect.attackIntervalMult;
        if (effect.pierceCountBonus !== undefined) this.bonusPierceCount += effect.pierceCountBonus;
        if (effect.rangeBonus !== undefined) this.bonusRange += effect.rangeBonus;
        if (effect.aoeRadiusBonus !== undefined) this.bonusAoeRadius += effect.aoeRadiusBonus;
        this.chainLevel++;
        this.draw();
    }

    update(time: number, delta: number, enemies: Enemy[]): void {
        this.attackTimer -= delta;
        if (this.attackTimer <= 0) {
            const target = this.findTarget(enemies);
            if (target) {
                this.attackTimer = this.config.attackInterval * this.attackIntervalMult / this.attackSpeedMult;
                this.fire(target);
            }
        }
    }

    private findTarget(enemies: Enemy[]): Enemy | null {
        const maxRange = this.range;
        const wallEdge = WALL_X + WALL_WIDTH;
        const candidates: Enemy[] = [];
        for (const enemy of enemies) {
            if (enemy.isDead) continue;
            if (enemy.enemyType === 'boulder') continue;
            if (enemy.isBurrowed) continue;
            if (enemy.isGoblin) continue;
            const dx = enemy.x - wallEdge;
            const dy = enemy.y - this.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist > maxRange) continue;
            candidates.push(enemy);
        }
        return selectByPriority(candidates, TARGETING[this.position], this.x, this.y);
    }

    private fire(target: Enemy): void {
        const proj = new Projectile(this.scene, this.x + 8, this.y, {
            damage: this.getDamage(),
            speed: this.config.projectileSpeed,
            piercing: this.pierceCount > 0,
            maxPierceCount: this.pierceCount,
            projectileType: 'arrow',
            targetX: target.x,
            targetY: target.y,
            aoeRadius: this.bonusAoeRadius > 0 ? this.bonusAoeRadius : undefined,
        });
        this.projectiles.push(proj);
    }

    draw(): void {
        this.graphics.clear();

        const x = this.x;
        const y = this.y;
        const isTop = this.position === 'top';

        // Draw crossbow frame — X-shaped bow on a wall mount
        const color = isTop ? 0x886633 : 0x664422;
        const accentColor = isTop ? 0xccaa44 : 0xaa8833;

        // Wall mount base
        this.graphics.fillStyle(0x555555, 1);
        this.graphics.fillRect(x - 4, y - 3, 6, 6);

        // Bow arms (V shape pointing right)
        this.graphics.lineStyle(2, color, 1);
        this.graphics.beginPath();
        this.graphics.moveTo(x, y - 8);
        this.graphics.lineTo(x + 6, y);
        this.graphics.lineTo(x, y + 8);
        this.graphics.strokePath();

        // String
        this.graphics.lineStyle(1, 0xdddddd, 0.8);
        this.graphics.beginPath();
        this.graphics.moveTo(x, y - 8);
        this.graphics.lineTo(x + 3, y);
        this.graphics.lineTo(x, y + 8);
        this.graphics.strokePath();

        // Bolt (loaded)
        this.graphics.lineStyle(1.5, accentColor, 1);
        this.graphics.beginPath();
        this.graphics.moveTo(x + 3, y);
        this.graphics.lineTo(x + 12, y);
        this.graphics.strokePath();

        // Bolt tip
        this.graphics.fillStyle(accentColor, 1);
        this.graphics.fillTriangle(x + 12, y - 2, x + 15, y, x + 12, y + 2);

        // Chain level pips
        if (this.chainLevel > 0) {
            this.graphics.fillStyle(0x4488ff, 1);
            for (let i = 0; i < Math.min(this.chainLevel, 5); i++) {
                this.graphics.fillCircle(x - 3 + i * 3, y + (isTop ? 12 : -12), 1.5);
            }
        }
    }

    destroy(): void {
        this.graphics.destroy();
    }
}
